


































































































import ContentCard from "@/components/ContentCard.vue";
import {
  computed,
  defineComponent,
  provide,
  reactive,
} from "@vue/composition-api";
import { ref, watch } from "@vue/composition-api";
import {
  useGetUsersQuery,
  useExportUserDataLazyQuery,
  UserFilterType,
  UserFiltersInput,
  UserSortInfo,
  UserField,
  OrderDirection,
  PageParamsGraphType,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import Table from "@/components/Table.vue";
import { TableField } from "@/types/TableField";
import useNavigateToUser from "@/composables/navigation/useNavigateToUser";
import { WithFilter } from "@/composables/useFilter";
import useProfile from "@/composables/useProfile";
import { useFromNowDateFormatted } from "@/composables/useDateFormat";
import { useNumberFormat } from "@/composables/useNumberFormat";
import { ParentQueryKey } from "./symbols";

export default defineComponent({
  components: {
    ContentCard,
    Table,
  },
  setup(_, { root }) {
    const isContainsTextSearch = ref(false);
    const hasLoadedExport = ref(false);
    const { isCentralAdmin, isLocalAdmin, isAppAdmin } = useProfile();

    const {
      load: loadExport,
      refetch: refetchExport,
      result: exportResult,
      loading: exportLoading,
    } = useExportUserDataLazyQuery({ fetchPolicy: "network-only" });

    watch(exportResult, (newValue) => {
      if (newValue?.exportUserData) {
        const { fileContent, fileName, contentType } = newValue.exportUserData;
        if (fileContent && fileName && contentType) {
          downloadBase64File(fileContent, fileName, contentType);
        }
      }
    });

    function downloadBase64File(
      base64Content: string,
      fileName: string,
      contentType: string
    ) {
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    function exportData() {
      if (!hasLoadedExport.value) {
        loadExport();
        hasLoadedExport.value = true;
        return;
      }

      refetchExport();
    }
    const firstLoading = ref(true);
    const filters = reactive<UserFiltersInput>({
      filterType: UserFilterType.All,
      email: "",
      name: "",
      organizationName: "",
      isContainTextSearch: isContainsTextSearch.value,
    });

    const sortInfo = reactive<UserSortInfo>({
      direction: OrderDirection.Asc,
      field: UserField.Name,
    });

    const pageParams = reactive<PageParamsGraphType>({
      pageSize: 50,
      currentPage: 1,
    });

    const { result, loading, error, document, variables } = useGetUsersQuery(
      () => ({
        filters: filters,
        sortInfo: sortInfo,
        pageParams: pageParams,
      })
    );

    provide(ParentQueryKey, {
      query: document.value,
      variables: variables.value,
    });

    function onActivateTab(type: UserFilterType) {
      pageParams.currentPage = 1;
      filters.filterType = type;
    }

    watch(
      () => useResult(result).value,
      (newValue) => {
        if (newValue) firstLoading.value = false;
      },
      { immediate: true }
    );

    watch(
      () => isContainsTextSearch.value,
      () => {
        filters.isContainTextSearch = isContainsTextSearch.value;
        pageParams.currentPage = 1;
      }
    );
    function filterFunction() {
      return true;
    }

    function filtersChanged(newFilters: WithFilter[]) {
      newFilters.forEach((filter) => {
        if (filter.key == "fullname" && filter.filter != filters.name)
          filters.name = filter.filter;
        if (filter.key == "email" && filter.filter != filters.email)
          filters.email = filter.filter;
        if (
          filter.key == "organization.name" &&
          filter.filter != filters.organizationName
        )
          filters.organizationName = filter.filter;

        pageParams.currentPage = 1;
      });
    }

    function sortByChanged(field: string) {
      if (field == "fullname") sortInfo.field = UserField.Name;
      if (field == "email") sortInfo.field = UserField.Email;
      if (field == "organization.name")
        sortInfo.field = UserField.OrganizationName;
      if (field == "lastLogin") sortInfo.field = UserField.LastSeen;
    }

    function sortDescChanged(desc: boolean) {
      sortInfo.direction = desc ? OrderDirection.Desc : OrderDirection.Asc;
    }

    return {
      tabs: computed<{ type: UserFilterType; title: string }[]>(() => [
        {
          title: root.$tc("users.tabs.all_users"),
          type: UserFilterType.All,
        },
        {
          title: root.$tc("users.tabs.owner_organization"),
          type: UserFilterType.Organization,
        },
        {
          title: root.$tc("users.tabs.on_behalf_off"),
          type: UserFilterType.AllBehalfOff,
        },
      ]),
      editable: computed(
        () => isCentralAdmin.value || isLocalAdmin.value || isAppAdmin.value
      ),
      onActivateTab,
      loading,
      error,
      firstLoading,
      pageParams,
      useFromNowDateFormatted,
      totalCount: useResult(result, undefined, (data) => data.users.totalCount),
      items: useResult(result, [], (data) => data.users.nodes),
      fields: computed<TableField[]>(() => [
        {
          key: "fullname",
          label: root.$tc("users.fields.name"),
          sortable: true,
          filter: true,
        },
        {
          key: "organization.name",
          label: root.$tc("users.fields.organisation"),
          sortable: true,
          filter: true,
        },
        {
          key: "email",
          label: root.$tc("users.fields.email"),
          sortable: true,
          filter: true,
        },
        {
          key: "lastLogin",
          label: root.$tc("users.fields.lastSeen"),
          sortable: true,
          filter: false,
          width: "10%",
        },
      ]),
      filterFunction,
      filtersChanged,
      sortByChanged,
      sortDescChanged,
      useNavigateToUser,
      isContainsTextSearch,
      useNumberFormat,
      exportLoading,
      exportData,
    };
  },
});
